import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Router } from '@angular/router';
import { take, takeUntil } from 'rxjs';
import { MemberAccess } from '../models/common/member-access.model';
import { LoginModel } from '../models/legacy';
import { AuthService } from '../services/auth.service';
import { SalesService } from '../services/sales.service';
import { UserService } from '../services/user.service';
import { BaseUnsubscribeComponent } from '../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import swal from 'sweetalert2';
import { RegisterService } from '../services/register.service';
import { ToastService } from '../shared-new/services/toast.service';

@Component({
  selector: 'login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
})
export class LoginPageComponent
  extends BaseUnsubscribeComponent
  implements OnInit {
  public loginForm: FormGroup;

  public loginSubmitting = false;
  public isLoader = false;
  public responseError: any = null;
  public showPassword = false;
  public user: LoginModel;
  public isLoginStatus: boolean;
  private additional: any;
  private validateAccountMessage = "Please check the email and follow the instructions to activate your account";
  public constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly saleService: SalesService,
    private readonly registerService: RegisterService,
    private readonly toastService: ToastService
  ) {
    super();
  }

  public loginFormSubmit(): void {
    this.loginSubmitting = !this.loginSubmitting;

    if (this.loginForm.valid) {
      this.responseError = null;
      this.isLoader = true;

      this.authService
        .login(this.loginForm.getRawValue().username, this.loginForm.getRawValue().password)
        .pipe(take(1))
        .subscribe(
          (response) => {
            if (this.authService.IsSuperUser()) {
              this.getMemberProfile(response);
            } else {
              this.performLoginProcess(response);
            }
          },
          (errorResponse: Error) => {
            this.isLoader = false;
            this.authService.clearLocalStorage();
            const errorData = JSON.parse(JSON.stringify(errorResponse));
            if (errorData?.error) {
              if (errorData?.status === 403 && errorData?.error.message.includes(this.validateAccountMessage)) {
                swal
                  .fire({
                    html: errorData?.error.message,
                    title: 'Resend Email',
                    confirmButtonText: "Resend",
                    showCancelButton: true,
                  })
                  .then(async (result) => {
                    if (result.isConfirmed) {
                      this.isLoader = true;
                      this.registerService.resendVerificationEmail(this.loginForm.getRawValue().username).pipe(takeUntil(this.unsubscribe$)).subscribe((response) => {
                        this.isLoader = false;
                        this.toastService.success("An email containing the account activation information has been sent to your email address.", "Email Sent!");
                      });
                    }
                  });
              }
              else if (errorData?.status === 401 || errorData?.status === 403 || errorData?.status === 400) {
                this.responseError = errorData;
              }

            }
          }
        );
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
  
  private performLoginProcess(response: LoginModel) {
    this.authService
      .getMemberAccessData()
      .pipe(take(1))
      .subscribe((accessDataResponse) => {
        this.authService.GlobalMemberAccessData =
          accessDataResponse as MemberAccess;
        this.getMemberProfile(response);
      }, (errorResponse: Error) => {
        this.isLoader = false;
        this.responseError = JSON.parse(JSON.stringify(errorResponse));
        this.authService.clearLocalStorage(); // TW-5272 in case of error the user was still getting logged in, removing the token in case of exception
      });
  }
  private getMemberProfile(response: LoginModel) {
    this.userService
      .getMemberProfile()
      .pipe(take(1))
      .subscribe((member) => {
        let user = this.authService.user;
        if (member.memberTimeZone) {
          user.timeZone = member.memberTimeZone;
        }
        if (member.memberId) {
          user.memberId = member.memberId;
        }
        this.authService.setToken(user);
        if (member.image) {
          this.authService.setUserImage(member.image);
        }

        if (this.authService.IsSuperUser()) {
          window.location.href = '/settings';
          return;
        }
        this.authService.setUserProfileStatus(member.hasBusinessProfile);
        if (!member.hasBusinessProfile) {
          window.location.href = '/profile/vet';
        }
        const return_url = localStorage.getItem("return_url");
        if (return_url) {
          localStorage.removeItem("return_url");
          window.location.href = `/${return_url}`;
          return;
        }
        const returnUrl = sessionStorage.getItem("returnUrl");
        if (returnUrl) {
          sessionStorage.removeItem("returnUrl");
          window.location.href = `${returnUrl}`;
          return;
        }
        const extras =
          this.router['lastSuccessfulNavigation']['extras'].state;
        if (extras && extras.encryptedSaleMemberAccessId) {
          this.additional = extras as {
            encryptedSaleMemberAccessId: string;
            saleId: number;
          };
        }
        if (response.accessToken && !this.authService.GlobalMemberAccessData.isSaleFeatureEnabled) {
          window.location.href = '/clinic-studies';
        }
        else if (
          this.additional &&
          this.additional.saleId > 0 &&
          response.accessToken
        ) {
          window.location.href = `/sales/${this.additional.saleId}`;
        } else if (response.accessToken) {
          window.location.href = '/sales';
        }
      }, (errorResponse: Error) => {
        this.isLoader = false;
        this.responseError = JSON.parse(JSON.stringify(errorResponse));
        this.authService.clearLocalStorage(); // TW-5272 in case of error the user was still getting logged in, removing the token in case of exception
      });
  }
  public userAuthenticate(): void {
    this.isLoginStatus = this.authService.isAuthenticated();
    this.authService.getUserData().subscribe((data) => {
      if (data) {
        this.user = JSON.parse(data);
      }
    });
  }

  public ngOnInit(): void {
    this.userAuthenticate();

    if (this.isLoginStatus == true) {
      this.router.navigate(['/sales']);
    }

    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required]],
      password: ['', Validators.required],
      clientInfo: [''],
    });
  }
}
